import { ClickAwayListener, IconButton, makeStyles, Tooltip } from '@material-ui/core'
import InfoOutlined from 'components/common/icons/InfoOutlined'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RetentionReferenceSources } from '../txTrackerConsts'
import { getReferenceFileName, readRetentionReference } from './retentionReferenceInfoUtils'

const useStyles = makeStyles({
  button: { marginLeft: 4, padding: 6 },
  details: { fontSize: 13, overflowWrap: 'anywhere', outline: 'none' }
})

const SOURCE_LABEL_KEYS = {
  [RetentionReferenceSources.PlannedFinalCopy]: 'plannedFinal',
  [RetentionReferenceSources.UploadedActualFinal]: 'uploadedFinal'
}

const isInsideTooltip = node => !!node?.closest?.('[role="tooltip"]')

const RetentionReferenceInfo = ({ referenceId }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const txTrackerRetentionStls = useSelector(state => state.treatmentReducer.treatment?.txTrackerRetentionStls)
  const reference = useMemo(
    () => readRetentionReference(txTrackerRetentionStls, referenceId),
    [txTrackerRetentionStls, referenceId]
  )

  useEffect(() => setOpen(false), [referenceId])

  const handleOpen = useCallback(() => setOpen(true), [])
  const handleClose = useCallback(() => setOpen(false), [])
  const handleEscape = useCallback(event => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      setOpen(false)
    }
  }, [])
  // The tooltip body is a portal, so it is neither inside the button's DOM subtree nor on
  // the ClickAwayListener's synthetic path. Selecting a filename inside it must not close it.
  const handleClickAway = useCallback(event => {
    if (!isInsideTooltip(event.target)) {
      setOpen(false)
    }
  }, [])
  const handleBlur = useCallback(event => {
    if (!isInsideTooltip(event.relatedTarget)) {
      setOpen(false)
    }
  }, [])

  const label = key => t(`dialogs.treatmentTracker.retentionReference.${key}`)
  const createdAt = reference?.createdAt && moment(reference.createdAt)
  const source = label(SOURCE_LABEL_KEYS[reference?.referenceSource] || 'unknownSource')
  const title = (
    // tabIndex lets a click inside the body move focus here, so the button's blur sees it.
    <div className={classes.details} tabIndex={-1} onKeyDown={handleEscape}>
      {reference ? (
        <>
          <div>{source}</div>
          {createdAt?.isValid() && (
            <div>
              {label('created')}: {createdAt.format('MMMM D, YYYY, HH:mm:ss Z')}
            </div>
          )}
          {['upper', 'lower'].map(arch => (
            <div key={arch}>
              {label(arch)}: {getReferenceFileName(reference.files?.[arch]) || label('fileUnavailable')}
            </div>
          ))}
        </>
      ) : (
        label('detailsUnavailable')
      )}
    </div>
  )

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Tooltip
        title={title}
        placement="top"
        arrow
        interactive
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        disableFocusListener
        disableTouchListener
      >
        <IconButton
          className={classes.button}
          size="small"
          aria-label={label('details')}
          aria-expanded={open}
          onFocus={handleOpen}
          onBlur={handleBlur}
          onClick={handleOpen}
          onKeyDown={handleEscape}
        >
          <InfoOutlined color="#727BFF" />
        </IconButton>
      </Tooltip>
    </ClickAwayListener>
  )
}

export default RetentionReferenceInfo
